import { Calendar, Clock, MapPin, Wine, GraduationCap, Heart, Users, Video, Shirt } from "lucide-react";
import { Reveal } from "./Reveal";
import { Flourish } from "./ornaments";
import { scrollToId } from "../lib/scroll";

const Meta = ({ icon: Icon, children }) => (
  <span className="flex items-center gap-2"><Icon size={14} className="text-gold-deep" /> {children}</span>
);

export const Events = () => (
  <section id="events" className="relative overflow-hidden bg-ivory py-24 text-navy-deep sm:py-32" data-testid="events-section">
    <div className="pointer-events-none absolute -right-8 top-8 w-48 opacity-25 sm:w-64" aria-hidden="true">
      <Flourish className="w-full" />
    </div>
    <div className="pointer-events-none absolute -left-8 bottom-8 w-48 opacity-25 sm:w-64" aria-hidden="true">
      <Flourish className="w-full" flip />
    </div>

    <div className="relative mx-auto max-w-5xl px-5 sm:px-8">
      <Reveal className="text-center">
        <p className="text-[11px] uppercase tracking-[0.4em] text-gold-deep">L'Itinéraire</p>
        <h2 className="mt-4 font-serif text-4xl font-semibold sm:text-5xl lg:text-6xl">Three moments of honour</h2>
        <p className="mx-auto mt-4 max-w-lg text-sm font-light text-navy/70 sm:text-base">
          Deux journées, trois façons de fêter ça.
        </p>
      </Reveal>

      <div className="mt-16 space-y-8">
        <Reveal>
          <article data-testid="event-card-celebration" className="overflow-hidden rounded-3xl border border-gold/60 bg-gradient-to-br from-white via-white to-[#F9F1DE] p-8 shadow-[0_18px_50px_rgba(153,101,21,0.12)] sm:p-12">
            <div className="grid gap-10 md:grid-cols-[1fr_180px] md:items-center">
              <div>
                <div className="flex flex-wrap items-center gap-4">
                  <span className="font-serif text-5xl font-light leading-none sm:text-6xl" style={{ background: "linear-gradient(135deg,#AA771C,#D4AF37 50%,#996515)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>01</span>
                  <div>
                    <p className="font-script text-3xl leading-none text-gold-deep">La Fête</p>
                    <h3 className="mt-1 font-serif text-3xl font-semibold sm:text-4xl">The Celebration</h3>
                  </div>
                </div>
                <span className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-navy px-3 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-gold-light">
                  <Wine size={11} /> Champagne soirée · Venez nombreux
                </span>
                <p className="mt-4 max-w-xl text-sm font-light leading-relaxed text-navy/70 sm:text-base">
                  This is the big one — champagne, music and dancing for Stephanie. RSVP,
                  bring a guest, and we'll send you the address.
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs uppercase tracking-[0.16em] text-navy/80 sm:text-[13px]">
                  <Meta icon={Calendar}>Saturday, December 5</Meta>
                  <Meta icon={Clock}>5:00 PM</Meta>
                  <Meta icon={MapPin}>Sarasota, Florida</Meta>
                  <span className="flex items-center gap-2 text-gold-deep"><Users size={14} /> Guests welcome</span>
                  <span className="flex items-center gap-2 text-gold-deep"><Shirt size={14} /> Elegant attire</span>
                </div>
                <button
                  data-testid="event-card-celebration-cta"
                  onClick={() => scrollToId("rsvp")}
                  className="mt-7 rounded-full bg-gradient-to-r from-gold-pale via-gold to-gold-deep px-8 py-3.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-navy-deep transition-all duration-300 hover:scale-[1.04] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] active:scale-95"
                >
                  RSVP
                </button>
              </div>
              <div className="hidden md:block">
                <div className="overflow-hidden rounded-t-full border-2 border-gold/50 p-1">
                  <img src="https://images.unsplash.com/photo-1590664017213-edec5d60c21e?crop=entropy&cs=srgb&fm=jpg&q=85&auto=format&fit=crop&w=400" alt="Two champagne flutes on white and gold" className="aspect-[3/4] w-full rounded-t-full object-cover" loading="lazy" />
                </div>
              </div>
            </div>
          </article>
        </Reveal>

        <Reveal>
          <article data-testid="event-card-commencement" className="relative overflow-hidden rounded-3xl bg-navy p-8 sm:p-12">
            <div className="pointer-events-none absolute -right-10 -top-10 opacity-[0.07]" aria-hidden="true">
              <GraduationCap size={260} strokeWidth={1} />
            </div>
            <div className="relative">
              <div className="flex flex-wrap items-center gap-4">
                <span className="text-gold-foil font-serif text-5xl font-light leading-none sm:text-6xl">02</span>
                <div>
                  <p className="font-script text-3xl leading-none text-gold-light">La Cérémonie</p>
                  <h3 className="mt-1 font-serif text-3xl font-semibold text-ivory sm:text-4xl">Commencement</h3>
                </div>
              </div>
              <span className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-gold/40 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-gold-light">
                <Video size={11} /> Only 4 tickets per graduate · Streamed live
              </span>
              <p className="mt-4 max-w-2xl text-sm font-light leading-relaxed text-mist sm:text-base">
                The official moment at Florida International University in Miami. FIU only gives
                each graduate four guest tickets, so we're streaming it live — tick the box in
                your RSVP and we'll send you the link.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs uppercase tracking-[0.16em] text-ivory/80 sm:text-[13px]">
                <span className="flex items-center gap-2"><Calendar size={14} className="text-gold" /> Tuesday, December 15</span>
                <span className="flex items-center gap-2"><Clock size={14} className="text-gold" /> 10:00 AM</span>
                <span className="flex items-center gap-2"><MapPin size={14} className="text-gold" /> FIU · Miami, Florida</span>
              </div>
              <button
                data-testid="event-card-commencement-cta"
                onClick={() => scrollToId("rsvp")}
                className="mt-7 rounded-full border-2 border-gold/50 px-8 py-3.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold-light transition-all duration-300 hover:border-gold hover:bg-gold/10"
              >
                Request the link
              </button>
            </div>
          </article>
        </Reveal>

        <Reveal>
          <article data-testid="event-card-dinner" className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#070D1F] via-[#0B132B] to-[#132338] p-8 text-ivory sm:p-12">
            <div className="grid gap-10 md:grid-cols-[1fr_auto] md:items-center">
              <div>
                <div className="flex flex-wrap items-center gap-4">
                  <span className="font-serif text-5xl font-light leading-none sm:text-6xl" style={{ background: "linear-gradient(135deg,#AA771C,#D4AF37 50%,#996515)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>03</span>
                  <div>
                    <p className="font-script text-3xl leading-none text-gold-light">Le Dîner · pour la famille</p>
                    <h3 className="mt-1 font-serif text-3xl font-semibold sm:text-4xl">Private Dinner</h3>
                  </div>
                </div>
                <span className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-gold-light">
                  <Heart size={11} /> Family only · By invitation
                </span>
                <p className="mt-4 max-w-xl text-sm font-light leading-relaxed text-ivory/70 sm:text-base">
                  That evening, it's just family — a private dinner at Mila in Miami for the
                  people who've been there since day one.
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs uppercase tracking-[0.16em] text-ivory/80 sm:text-[13px]">
                  <span className="flex items-center gap-2"><Calendar size={14} className="text-gold" /> Tuesday, December 15</span>
                  <span className="flex items-center gap-2"><Clock size={14} className="text-gold" /> 6:00 PM</span>
                  <span className="flex items-center gap-2"><MapPin size={14} className="text-gold" /> Mila · Miami, Florida</span>
                </div>
              </div>
              <p className="hidden max-w-[180px] text-right font-script text-4xl leading-snug text-gold-light/80 md:block">
                une soirée en famille
              </p>
            </div>
          </article>
        </Reveal>
      </div>
    </div>
  </section>
);
